import {
    ISameCaseMatchedCasesSearchModel,
    ShowAllNewAndChangesSameCasesDisplayLabel,
    ShowAllRejectedSameCasesDisplayLabel,
    ShowOnlyRejectedSameCasesDisplayLabel,
} from '../../../pages/quad/same-cases-admin/manage/useGetSameCasesQuery';

import { AppliedFiltersSection } from './AppliedFiltersSection';
import { AppliedFiltersView } from './AppliedFiltersView';
import { FilterBadge } from './FilterBadge';
import { ISelectedFiltersDetails } from './AppliedFiltersDetailsBody';
import { isEmpty } from 'lodash';
import { statusOptions } from '../../quad/same-cases-admin/manage/SameCaseManageSearchFilters';
import { useGetJurisdictions } from '../../../shared/react-query-hooks/useGetJurisdictions';
import { useGetUsStatesLookup } from '../../../shared/react-query-hooks/useUsStatesLookup';
import { useState } from 'react';

interface ISameCaseAppliedFiltersProps {
    currentFilters: ISameCaseMatchedCasesSearchModel;
    onApplyFilters: (filters: ISameCaseMatchedCasesSearchModel) => void;
    onToggleFilterPane: () => void;
}

export function SameCaseAppliedFilters(props: ISameCaseAppliedFiltersProps) {
    const jurisdictionsLookup = useGetJurisdictions(props.currentFilters.caseFileModelId);
    const usStatesLookup = useGetUsStatesLookup();

    const handleRemoveState = (stateGuid: string) => {
        const filteredStateGuids = props.currentFilters.states?.filter((j) => j !== stateGuid);
        const newFilters = { ...props.currentFilters, states: filteredStateGuids };

        const selectedState = usStatesLookup.data?.find((x) => x.guid === stateGuid);

        // If at least one State filter remains, remove all jurisdictions that belonged to removed state
        if (filteredStateGuids?.length ?? 0 > 0) {
            const toRemove = props.currentFilters.jurisdictions?.filter((x) => {
                const found = (jurisdictionsLookup.data || []).find((j) => j.guid === x);

                return found?.name?.slice(-2) === selectedState?.name;
            });

            newFilters.jurisdictions =
                newFilters.jurisdictions?.filter((x) => !toRemove?.includes(x)) ?? [];
        }

        props.onApplyFilters(newFilters);
    };

    const handleRemoveJurisdiction = (jurisdictionGuid: string) => {
        const filteredJurisdictionGuids = props.currentFilters.jurisdictions?.filter(
            (j) => j !== jurisdictionGuid
        );
        const newFilters = { ...props.currentFilters, jurisdictions: filteredJurisdictionGuids };
        props.onApplyFilters(newFilters);
    };

    const handleRemoveCaseNumber = () => {
        const newFilters = { ...props.currentFilters, caseNumber: undefined };
        props.onApplyFilters(newFilters);
    };

    const handleRemoveName = () => {
        const newFilters = { ...props.currentFilters, name: undefined };
        props.onApplyFilters(newFilters);
    };

    const handleRemoveStatus = (statusId: number) => {
        const filteredStatusIds = props.currentFilters.statuses?.filter((j) => j !== statusId);
        const newFilters = { ...props.currentFilters, statuses: filteredStatusIds, showAllNewAndChangesSameCases: false };
        props.onApplyFilters(newFilters);
    };

    const handleRemoveAllNewAndChangesFlag = () => {
        const newFilters: ISameCaseMatchedCasesSearchModel = {
            ...props.currentFilters,
            showAllNewAndChangesSameCases: false,
        };
        props.onApplyFilters(newFilters);
    };

    const handleRemoveAllRejectedFlag = () => {
        const newFilters: ISameCaseMatchedCasesSearchModel = {
            ...props.currentFilters,
            showAllRejectedSameCases: false,
        };
        props.onApplyFilters(newFilters);
    };

    const handleRemoveOnlyRejectedFlag = () => {
        const newFilters: ISameCaseMatchedCasesSearchModel = {
            ...props.currentFilters,
            showOnlyRejectedSameCases: false,
        };
        props.onApplyFilters(newFilters);
    };

    const sortedSelectedStates =
        props.currentFilters.states
            ?.map((guid) => usStatesLookup.data?.find((lu) => lu.guid === guid))
            .sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? '')) ?? [];

    const sortedSelectedJurisdictions =
        props.currentFilters.jurisdictions
            ?.map((guid) => jurisdictionsLookup.data?.find((lu) => lu.guid === guid))
            .sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? '')) ?? [];

    const selectedStatuses = props.currentFilters.statuses?.map((item) => {
        const status = statusOptions?.find((s) => s.id === item);
        return status ? status : { id: 0, label: '', value: '' };
    });

    const details: ISelectedFiltersDetails[] = [
        {
            label: 'Statuses',
            values: selectedStatuses?.map((s) => s?.label ?? '') ?? [],
        },
        {
            label: 'States',
            values: sortedSelectedStates?.map((s) => s?.displayName ?? s?.name ?? '') ?? [],
        },
        {
            label: 'Jurisdictions',
            values: sortedSelectedJurisdictions.map((j) => j?.displayName ?? j?.name ?? '') ?? [],
        },
        {
            label: 'Case Number',
            values: props.currentFilters.caseNumber ? [props.currentFilters.caseNumber] : [],
        },
        {
            label: 'Case Name',
            values: props.currentFilters.name ? [props.currentFilters.name] : [],
        },
    ];

    const noFiltersAreApplied =
        isEmpty(props.currentFilters.states) &&
        isEmpty(props.currentFilters.jurisdictions) &&
        isEmpty(props.currentFilters.caseNumber) &&
        isEmpty(props.currentFilters.name);

    return (
        <AppliedFiltersSection onToggleFilterPane={props.onToggleFilterPane}>
            <AppliedFiltersView
                selectedFilterDetails={details}
                hideSelectedFiltersIcon={noFiltersAreApplied}
            >
                {selectedStatuses?.map((item) => {
                    return (
                        <FilterBadge
                            key={item?.id}
                            label={item?.label ?? ''}
                            onRemove={() => handleRemoveStatus(item?.id ?? 0)}
                        />
                    );
                })}
                {!!props.currentFilters?.showAllNewAndChangesSameCases && (
                    <FilterBadge
                        key={'all-new-and-changes-filter'}
                        label={ShowAllNewAndChangesSameCasesDisplayLabel}
                        onRemove={handleRemoveAllNewAndChangesFlag}
                    />
                )}
                {sortedSelectedStates.map((item) => (
                    <FilterBadge
                        key={item?.guid}
                        label={item?.displayName ?? item?.name ?? ''}
                        onRemove={() => handleRemoveState(item?.guid ?? '')}
                        isLoading={usStatesLookup.isLoading}
                    />
                ))}
                {sortedSelectedJurisdictions.map((item) => (
                    <FilterBadge
                        key={item?.guid}
                        label={item?.displayName ?? item?.name ?? ''}
                        onRemove={() => handleRemoveJurisdiction(item?.guid ?? '')}
                        isLoading={jurisdictionsLookup.isLoading}
                    />
                ))}
                {props.currentFilters?.caseNumber && (
                    <FilterBadge
                        key={'case-number-filter'}
                        label={props.currentFilters.caseNumber}
                        onRemove={handleRemoveCaseNumber}
                    />
                )}
                {props.currentFilters?.name && (
                    <FilterBadge
                        key={'name-filter'}
                        label={props.currentFilters.name}
                        onRemove={handleRemoveName}
                    />
                )}
                {!!props.currentFilters?.showAllRejectedSameCases && (
                    <FilterBadge
                        key={'all-rejected-filter'}
                        label={ShowAllRejectedSameCasesDisplayLabel}
                        onRemove={handleRemoveAllRejectedFlag}
                    />
                )}
                {!!props.currentFilters?.showOnlyRejectedSameCases && (
                    <FilterBadge
                        key={'only-rejected-filter'}
                        label={ShowOnlyRejectedSameCasesDisplayLabel}
                        onRemove={handleRemoveOnlyRejectedFlag}
                    />
                )}
            </AppliedFiltersView>
        </AppliedFiltersSection>
    );
}
